import {
  GridExcelExportOptions,
  GridApiPremium,
  GridRowId,
  gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid-premium'

const isProductId = (id: GridRowId) => typeof id === 'string' && id.includes('::PRODUCT_')
const orgIdOf = (id: GridRowId) => (typeof id === 'string' ? id.split('::')[0] : String(id))
const EXCLUDE_EXPORT_FIELDS = new Set<string>([
  '__check__', // checkbox selection column
  '__row_reorder__', // row reorder drag handle (if used)
  '__detail_panel_toggle__', // detail panel toggle (if used)
  '__tree_data_group__', // tree/grouping col (often hidden anyway)
])
type ParentTotalMode = 'blank' | 'value' | 'formula'
type ExportScope = 'visible' | 'org-all-kids'

export function getVisibleOrgThenProductIds(apiRef: React.RefObject<GridApiPremium>): GridRowId[] {
  const ordered = gridFilteredSortedRowIdsSelector(apiRef) // ✅ only rows visible after filters/sort/quick filter
  // keep just your real data rows
  return ordered.filter(id => {
    const r = apiRef.current.getRow(id)
    return r?.isParent || r?.isProduct
  })
}

export function getOrgThenProductIds(apiRef: React.RefObject<GridApiPremium>): GridRowId[] {
  const ordered = apiRef.current.getSortedRowIds?.() ?? [] // fallback if you prefer
  const orderIndex = new Map(ordered.map((id, i) => [id, i]))
  const byOrder = (a: GridRowId, b: GridRowId) => (orderIndex.get(a) ?? 1e9) - (orderIndex.get(b) ?? 1e9)

  const allIds: GridRowId[] = apiRef.current.getAllRowIds()
  console.log('allIds', allIds)
  const rowIds = gridFilteredSortedRowIdsSelector(apiRef)
  console.log('rowIds', rowIds)
  const orderedSet = new Set(ordered)

  const productsByOrg = new Map<string, GridRowId[]>()
  for (const pid of rowIds) {
    if (!isProductId(pid)) continue
    const oid = orgIdOf(pid)
    if (!(orderedSet.has(pid) || orderedSet.has(oid))) continue
    ;(productsByOrg.get(oid) ?? productsByOrg.set(oid, []).get(oid)!).push(pid)
  }

  const out: GridRowId[] = []
  for (const id of ordered) {
    if (isProductId(id)) continue // orgs only here
    out.push(id)
    const kids = (productsByOrg.get(String(id)) ?? []).slice().sort(byOrder)
    out.push(...kids)
  }
  return out
}

const colLetter = (i: number) => {
  let s = ''
  while (i > 0) {
    const m = (i - 1) % 26
    s = String.fromCharCode(65 + m) + s
    i = Math.floor((i - 1) / 26)
  }
  return s
}

export const exportXlsx = async (
  _context: string,
  apiRef: React.RefObject<GridApiPremium>,
  NUMERIC_FIELDS: string[],
  NAME_FIELD: string = 'name',
  fileName: string = 'organizations',
  parentTotals: ParentTotalMode = 'value',
  scope: ExportScope = 'visible'
) => {
  const ids =
    scope === 'visible'
      ? getVisibleOrgThenProductIds(apiRef) // ✅ EXACTLY what’s on screen
      : getOrgThenProductIds(apiRef) // previous behavior (parent + all kids)

  const visibleFields = apiRef.current
    .getVisibleColumns()
    .filter(c => !c.disableExport && !EXCLUDE_EXPORT_FIELDS.has(c.field))
    .map(c => c.field)
  // map field -> 1-based column
  const colIndex = new Map<string, number>()
  visibleFields.forEach((f, i) => colIndex.set(f, i + 1))

  const nameCol = colIndex.get(NAME_FIELD)
  const numericCols = NUMERIC_FIELDS.map(f => colIndex.get(f)).filter((n): n is number => typeof n === 'number')
  const numericFieldsPresent = NUMERIC_FIELDS.filter(f => colIndex.has(f))
  const n = (v: any) => (typeof v === 'number' ? v : Number(v) || 0)
  const round2 = (v: number) => Math.round((v + 1e-12) * 100) / 100

  await apiRef.current.exportDataAsExcel({
    fileName: fileName,
    fields: visibleFields,
    includeHeaders: true,
    getRowsToExport: () => ids,
    columnsStyles: Object.fromEntries(NUMERIC_FIELDS.map(f => [f, { numFmt: '#,##0.00' }])),

    exceljsPostProcess: async ({ worksheet }) => {
      // Freeze header
      worksheet.views = [{ state: 'frozen', ySplit: 1 }]

      // Header look
      const header = worksheet.getRow(1)
      header.font = { ...(header.font ?? {}), bold: true, size: 13 }
      header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } }
      header.height = 22

      // Colors (tweak to taste)
      const PRODUCT_NAME_COLOR = 'FF6B7280'
      const ORG_NAME_COLOR = 'FF111827'

      // Walk blocks: parent -> its products
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i]
        const model = apiRef.current.getRow(id)
        const rowNum = i + 2 // +1 header, +1 1-based
        const row = worksheet.getRow(rowNum)

        const isParent = !!model?.isParent
        const isProduct = !!model?.isProduct

        // Outline levels for Excel group +/- UI
        row.outlineLevel = isProduct ? 1 : 0

        if (isParent) {
          // Style parent row
          row.font = { ...(row.font ?? {}), bold: true, color: { argb: ORG_NAME_COLOR } }

          // Find contiguous child block (until next parent or end)
          let j = i + 1
          while (j < ids.length) {
            const nextModel = apiRef.current.getRow(ids[j])
            if (nextModel?.isParent) break
            j++
          }
          const firstChildIdx = i + 1
          const lastChildIdx = j - 1
          const hasChildren = firstChildIdx <= lastChildIdx

          if (!hasChildren) {
            // no children -> either blank or zero
            if (parentTotals === 'blank') {
              for (const c of numericCols) row.getCell(c).value = null
            } else {
              for (const c of numericCols) row.getCell(c).value = 0
            }
          } else {
            if (parentTotals === 'blank') {
              // Keep blank numeric cells on parent
              for (const c of numericCols) row.getCell(c).value = null
            } else if (parentTotals === 'value') {
              // Static value: sum children from the DATA (not the cells)
              const sums: Record<string, number> = Object.fromEntries(numericFieldsPresent.map(f => [f, 0]))
              for (let k = firstChildIdx; k <= lastChildIdx; k++) {
                const child = apiRef.current.getRow(ids[k])
                if (!child?.isProduct) continue
                for (const f of numericFieldsPresent) {
                  sums[f] += n((child as any)[f])
                }
              }
              // write to parent row
              for (const f of numericFieldsPresent) {
                const c = colIndex.get(f)!
                row.getCell(c).value = round2(sums[f])
              }
            } else if (parentTotals === 'formula') {
              // Live formula: SUBTOTAL over the child rows (respects filters/outlines)
              const startR = firstChildIdx + 2 // row # of first child in sheet
              const endR = lastChildIdx + 2
              for (const c of numericCols) {
                const L = colLetter(c)
                row.getCell(c).value = { formula: `SUBTOTAL(9, ${L}${startR}:${L}${endR})` } // 9 = SUM
              }
            }
          }
        } else if (isProduct && nameCol) {
          // Optional: visual cue for product rows
          const nameCell = row.getCell(nameCol)
          nameCell.alignment = { ...(nameCell.alignment ?? {}), horizontal: 'left', indent: 1 }
          nameCell.font = { ...(nameCell.font ?? {}), color: { argb: PRODUCT_NAME_COLOR } }
        }
      }

      // Simple auto-fit for visible columns
      visibleFields.forEach((_, idx) => {
        const col = idx + 1
        let maxLen = String(header.getCell(col).value ?? '').length
        for (let r = 2; r <= worksheet.rowCount; r++) {
          const v = worksheet.getRow(r).getCell(col).value
          const s =
            v == null
              ? ''
              : typeof v === 'object' && 'text' in (v as any)
              ? (v as any).text
              : typeof v === 'object' && 'result' in (v as any)
              ? String((v as any).result ?? '')
              : String(v)
          if (s.length > maxLen) maxLen = s.length
        }
        worksheet.getColumn(col).width = Math.min(Math.max(10, maxLen + 2), 60)
      })
    },
  } as GridExcelExportOptions)
}
